/**
 * Os detalhes de uma conta na área de administração (docs/spec/admin.md): os dados, a suspensão,
 * as mesas, o histórico e os botões de ação. No computador fica à direita da lista; no celular,
 * ocupa a tela, com o voltar.
 */
import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { useT } from '../i18n/index.js';
import { formatBytes } from '../bytes.js';
import type { AdminUserDetail } from '../shared.js';
import { Avatar } from '../social/Avatar.js';
import { describeError, useApp } from '../store.js';
import { Button, Spinner } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import { useMobile } from '../ui/mobile.js';
import { availableActions, logDetailText } from './adminModel.js';
import { usePlanName } from '../plans/usePlanName.js';
import { useDateTimeText } from './AdminLog.js';
import { AdminBadges, useDateText, useSeenText } from './AdminUsers.js';
import { useAdminActions } from './useAdminActions.js';

export function AdminUserPanel({ userId, version, onChanged, onClose }: { userId: string; version: number; onChanged: () => void; onClose: () => void }) {
  const t = useT();
  const token = useApp((s) => s.token);
  const meId = useApp((s) => s.user?.id ?? '');
  const run = useAdminActions();
  const seen = useSeenText();
  const date = useDateText();
  const when = useDateTimeText();
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const mobile = useMobile();
  const planName = usePlanName();

  useEffect(() => {
    if (!token) return;
    let alive = true;
    api.adminUser(token, userId).then(
      (result) => {
        if (!alive) return;
        setDetail(result.user);
        setError(null);
      },
      (failure: unknown) => {
        if (alive) setError(describeError(failure));
      },
    );
    return () => {
      alive = false;
    };
  }, [token, userId, version]);

  const name = detail?.user.displayName ?? '';

  return (
    <aside className="rd-adminPanel" aria-label={name ? t('admin.panel.label', { name }) : t('admin.details')}>
      <div className="rd-adminPanel__top">
        {/* no celular, os detalhes cobrem a lista: o botão é o voltar */}
        <button type="button" className="rd-iconBtn rd-adminPanel__close" aria-label={t('admin.panel.close')} onClick={onClose}>
          <Icon name={mobile ? 'chevronLeft' : 'close'} size={mobile ? 22 : 16} />
        </button>
      </div>
      {error ? (
        <div className="rd-placeholder">
          <p role="alert">{error}</p>
        </div>
      ) : null}
      {!detail && !error ? (
        <div className="rd-placeholder">
          <Spinner size={28} />
        </div>
      ) : null}
      {detail ? (
        <div className="rd-adminPanel__body">
          <div className="rd-adminPanel__who">
            <Avatar name={name} color={detail.user.color} size={56} userId={detail.user.id} avatarVersion={detail.user.avatarVersion} />
            <div>
              <h3 className="rd-adminPanel__name">{name}</h3>
              <div className="rd-adminPanel__badges">
                <AdminBadges row={detail} />
              </div>
            </div>
          </div>

          <div className="rd-adminPanel__actions">
            {availableActions(detail, meId).map(({ action, refusal }) => (
              <Button
                key={action}
                className={action === 'delete' || action === 'suspend' ? 'rd-btn--danger' : undefined}
                disabled={refusal !== null || busy}
                title={refusal ? t(`admin.refusal.${refusal}`) : undefined}
                onClick={async () => {
                  setBusy(true);
                  const done = await run(detail, action);
                  setBusy(false);
                  if (!done) return;
                  // a conta apagada não tem mais detalhes
                  if (action === 'delete') onClose();
                  onChanged();
                }}
              >
                {t(`admin.action.${action}`)}
              </Button>
            ))}
          </div>

          {detail.suspension ? (
            <div className="rd-adminPanel__suspension" role="note">
              <strong>{t('admin.panel.suspendedBy', { name: detail.suspension.byName, date: when(detail.suspension.at) })}</strong>
              <span>{detail.suspension.reason ? t('admin.panel.reason', { reason: detail.suspension.reason }) : t('admin.panel.noReason')}</span>
            </div>
          ) : null}

          <dl className="rd-adminPanel__facts">
            <dt>{t('admin.col.email')}</dt>
            <dd>{detail.email ?? '—'}</dd>
            <dt>{t('admin.panel.created')}</dt>
            <dd>{date(detail.user.createdAt)}</dd>
            <dt>{t('admin.col.seen')}</dt>
            <dd className={detail.online ? 'is-online' : undefined}>{seen(detail)}</dd>
            <dt>{t('admin.panel.lastLogin')}</dt>
            <dd>{detail.lastLoginAt ? when(detail.lastLoginAt) : '—'}</dd>
            <dt>{t('admin.panel.sessions')}</dt>
            <dd>{detail.sessions}</dd>
            <dt>{t('admin.panel.tos')}</dt>
            <dd>{detail.tosAcceptedAt ? t('admin.panel.tosAccepted', { version: detail.tosVersion, date: date(detail.tosAcceptedAt) }) : t('admin.panel.tosNone')}</dd>
            <dt>{t('admin.col.plan')}</dt>
            <dd className="rd-adminPanel__plan">
              <span>{planName(detail.plan.id)}</span>
              {detail.planRecord ? (
                <span className="rd-adminPanel__muted">
                  {[
                    detail.planRecord.expired
                      ? t('admin.plan.expiredOn', { plan: planName(detail.planRecord.planId), date: date(detail.planRecord.expiresAt ?? 0) })
                      : detail.planRecord.expiresAt === null
                        ? t('admin.plan.noExpiryShort')
                        : t('admin.plan.until', { date: date(detail.planRecord.expiresAt) }),
                    detail.planRecord.grantedByName ? t('admin.plan.grantedBy', { name: detail.planRecord.grantedByName }) : null,
                    detail.planRecord.note || null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              ) : null}
            </dd>
            <dt>{t('admin.col.space')}</dt>
            <dd>{t('admin.panel.space', { used: formatBytes(detail.usedBytes), quota: formatBytes(detail.quotaBytes), files: detail.files })}</dd>
            <dt>{t('admin.panel.friends')}</dt>
            <dd>{detail.friends}</dd>
          </dl>

          <h4 className="rd-adminPanel__section">{t('admin.panel.rooms', { n: detail.rooms.length })}</h4>
          {detail.rooms.length ? (
            <ul className="rd-adminPanel__list">
              {detail.rooms.map((room) => (
                <li key={room.id}>
                  <span>{room.name}</span>
                  <span className="rd-adminPanel__muted">{date(room.createdAt)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="rd-adminPanel__muted">{t('admin.panel.noRooms')}</p>
          )}

          <h4 className="rd-adminPanel__section">{t('admin.panel.history')}</h4>
          {detail.history.length ? (
            <ul className="rd-adminPanel__list rd-adminPanel__history">
              {detail.history.map((entry) => (
                <li key={entry.seq}>
                  <span>
                    <strong>{entry.adminName}</strong> · {t(`admin.log.${entry.action}`)}
                    {entry.detail ? <span className="rd-adminPanel__muted"> — {logDetailText(t, entry, date, planName)}</span> : null}
                  </span>
                  <span className="rd-adminPanel__muted">{when(entry.at)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="rd-adminPanel__muted">{t('admin.panel.noHistory')}</p>
          )}
        </div>
      ) : null}
    </aside>
  );
}
